import React, { useState, useEffect } from 'react';
import Loader from '../../../components/Loader/Loader';
import { getRiskAsessment, postRiskMatrixUpdate } from '../../../apiservices/ApiServices';

const severityOptions = [
    { name: 'Negligible', score: 1 },
    { name: 'Minor', score: 2 },
    { name: 'Major', score: 3 },
    { name: 'Hazardous', score: 4 },
    { name: 'Catastrophic', score: 5 }
];

const likelihoodOptions = [
    { name: 'Unlikely', score: 1 },
    { name: 'Rare', score: 2 },
    { name: 'Possible', score: 3 },
    { name: 'Likely', score: 4 },
    { name: 'Frequent', score: 5 }
];

const RiskMatrix = ({ details, postUpdateData, toast }) => {
    const [isLoadings, setLoadings] = useState(false);
    const [riskAsessmentColor, setRiskAsessmentColor] = useState([]);
    const [riskMatrix, setRiskMatrix] = useState([
        {
            RiskMatrixID: '',
            Hazard: '',
            Severity: 0,
            Likelihood: 0,
            SeverityName: '',
            LikelihoodName: '',
            RiskRating: 0,
            RiskCategory: 'Low',
            RiskCategoryColor: '#b5dfb5'
        }
    ]);

    const getRiskAsessments = async () => {
        try {
            const res = await getRiskAsessment();
            setRiskAsessmentColor(res.data.data);
        } catch (error) {
            console.log(error?.response?.data?.data);
        }
    };

    const getRiskColor = (riskRating) => {
        if (Array.isArray(riskAsessmentColor) && riskAsessmentColor.length > 0) {
            if (riskRating >= 16) return riskAsessmentColor[0].RiskColorCode;  //Critical
            if (riskRating >= 10) return riskAsessmentColor[1].RiskColorCode; //High
            if (riskRating >= 5) return riskAsessmentColor[2].RiskColorCode; //Medium
            return riskAsessmentColor[3].RiskColorCode; //Low
        }

        if (riskRating >= 16) return '#f62000';  //Critical
        if (riskRating >= 10) return '#f9c106'; //High
        if (riskRating >= 5) return '#fcff00'; //Medium
        return '#34cd00';                     // Low
    };


    useEffect(() => {
        getRiskAsessments();
    }, []);


    useEffect(() => {
        if (details?.risk_matrix && Array.isArray(details?.risk_matrix)) {
            const updatedMatrix = details?.risk_matrix.map(item => {
                const Severity = parseInt(item.Severity || 0);
                const Likelihood = parseInt(item.Likelihood || 0);
                const rating = parseInt(item.RiskRating || Severity * Likelihood);

                return {
                    RiskMatrixID: item.RiskMatrixID,
                    Hazard: item.Hazard,
                    Severity: item.Severity,
                    Likelihood: item.Likelihood,
                    SeverityName: item.SeverityName || (severityOptions.find(s => s.score === Severity)?.name || ''),
                    LikelihoodName: item.LikelihoodName || (likelihoodOptions.find(l => l.score === Likelihood)?.name || ''),
                    RiskRating: rating,
                    RiskCategory: item.RiskCategory || (rating >= 16 ? 'Critical' : rating >= 10 ? 'High' : rating >= 5 ? 'Medium' : 'Low'),
                    RiskCategoryColor: getRiskColor(rating),
                };
            });
            setRiskMatrix(updatedMatrix);
        }
    }, [details]);

    const handleRiskMatrixChange = (index, field, value) => {
        const updatedMatrix = [...riskMatrix];
        updatedMatrix[index] = { ...updatedMatrix[index], [field]: value };

        if (field === 'SeverityName') {
            const selectedSeverity = severityOptions.find(s => s.name === value);
            if (selectedSeverity) {
                updatedMatrix[index].Severity = selectedSeverity.score;
            }
        } else if (field === 'Severity') {
            const selectedSeverity = severityOptions.find(s => s.score === parseInt(value));
            if (selectedSeverity) {
                updatedMatrix[index].SeverityName = selectedSeverity.name;
            }
        } else if (field === 'LikelihoodName') {
            const selectedLikelihood = likelihoodOptions.find(l => l.name === value);
            if (selectedLikelihood) {
                updatedMatrix[index].Likelihood = selectedLikelihood.score;
            }
        } else if (field === 'Likelihood') {
            const selectedLikelihood = likelihoodOptions.find(l => l.score === parseInt(value));
            if (selectedLikelihood) {
                updatedMatrix[index].LikelihoodName = selectedLikelihood.name;
            }
        }

        if (['SeverityName', 'Severity', 'LikelihoodName', 'Likelihood'].includes(field)) {
            const severityVal = updatedMatrix[index].Severity;
            const likelihoodVal = updatedMatrix[index].Likelihood;
            const rating = severityVal * likelihoodVal;

            let category = 'Low';
            if (rating >= 16) category = 'Critical';
            else if (rating >= 10) category = 'High';
            else if (rating >= 5) category = 'Medium';

            updatedMatrix[index].RiskRating = rating;
            updatedMatrix[index].RiskCategory = category;
            updatedMatrix[index].RiskCategoryColor = getRiskColor(rating);
        }

        setRiskMatrix(updatedMatrix);
    };

    const submitData = async () => {
        const formattedRiskMatrix = riskMatrix.map(risk => ({
            RiskMatrixID: risk.RiskMatrixID,
            Hazard: risk.Hazard,
            Severity: risk.Severity,
            Likelihood: risk.Likelihood,
            SeverityName: risk.SeverityName,
            LikelihoodName: risk.LikelihoodName,
            RiskRating: risk.RiskRating,
            RiskCategory: risk.RiskCategory,
            RiskCategoryColor: risk.RiskCategoryColor
        }));

        const postdata = {
            incident_header_id: details?.header_id,
            risk_matrix: formattedRiskMatrix,
        };

        setLoadings(true);
        try {
            const res = await postRiskMatrixUpdate(postdata);
            toast.success(res.data.message);
            postUpdateData();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Something went wrong');
        } finally {
            setLoadings(false);
        }
    };

    return (
        <>
            <div className="form-box">
                <div className="fieldset RiskMatrix">
                    <h1>Initial Risk Matrix</h1>
                    <div className="formsBox">
                        <div className='RiskscoreList' style={{ marginBottom: 5 }}>
                            <ul>
                                {riskAsessmentColor.map((item) => (
                                    <li key={item.ToScore}><span style={{ backgroundColor: item.RiskColorCode }}></span> {item.RiskDes}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="table-responsive">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Hazard</th>
                                        <th>Severity</th>
                                        <th>Likelihood</th>
                                        <th>Risk Rating</th>
                                        <th>Risk Category</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {riskMatrix.map((risk, index) => (
                                        <tr key={index}>
                                            <td style={{ backgroundColor: risk.RiskCategoryColor }}>
                                                <textarea
                                                    type="text"
                                                    className="form-control"
                                                    value={risk.Hazard}
                                                    onChange={e => handleRiskMatrixChange(index, 'Hazard', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <select
                                                    value={risk.SeverityName}
                                                    onChange={e => handleRiskMatrixChange(index, 'SeverityName', e.target.value)}
                                                    className="form-control"
                                                >
                                                    <option value="" disabled={true}>Select Severity</option>
                                                    {severityOptions.map(option => (
                                                        <option key={option.name} value={option.name}>
                                                            {option.name} ({option.score})
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <select
                                                    value={risk.LikelihoodName}
                                                    onChange={e => handleRiskMatrixChange(index, 'LikelihoodName', e.target.value)}
                                                    className="form-control"
                                                >
                                                    <option value="" disabled={true}>Select Likelihood</option>
                                                    {likelihoodOptions.map(option => (
                                                        <option key={option.name} value={option.name}>
                                                            {option.name} ({option.score})
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>{risk.RiskRating}</td>
                                            <td>
                                                <div className='chips' style={{ backgroundColor: risk.RiskCategoryColor }}>{risk.RiskCategory}</div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="button-boxs grid pb-2">
                        <button type="submit" onClick={submitData} className="btn btn-orange">Submit</button>
                    </div>
                </div>
            </div>
            {isLoadings && <Loader />}
        </>
    );
};

export default RiskMatrix;